import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Row, Col, Alert, ProgressBar } from 'react-bootstrap';
import { FaUpload, FaCheckCircle } from 'react-icons/fa';
import './ProductDevContactForm.css';

const ProductDevContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '3d-printing',
    material: '',
    quantity: 1,
    projectDescription: ''
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileKey, setUploadedFileKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [turnstileError, setTurnstileError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const turnstileWidgetId = useRef(null);
  const turnstileRef = useRef(null);

  // Get environment variables
  const CLOUDFLARE_SITE_KEY = process.env.REACT_APP_CLOUDFLARE_SITE_KEY;
  const UPLOAD_API_URL = process.env.REACT_APP_UPLOAD_API_URL || '/api/upload-request';
  const CONTACT_API_URL = process.env.REACT_APP_API_URL || '/api/contact';
  const MAX_FILE_SIZE_MB = parseInt(process.env.REACT_APP_MAX_FILE_SIZE_MB || '150');
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const ALLOWED_EXTENSIONS = ['.stl', '.obj', '.step', '.stp', '.iges', '.igs', '.3mf', '.amf', '.brep', '.zip'];

  const initialized = useRef(false);

  const isDevelopment = process.env.NODE_ENV === 'development' ||
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';

  // Initialize Turnstile
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (isDevelopment) {
      console.log('Development mode: Setting mock Turnstile token');
      setTurnstileToken('dev-token');
      return;
    }

    if (!CLOUDFLARE_SITE_KEY) {
      console.error('No Cloudflare Turnstile site key provided');
      setTurnstileError(true);
      return;
    }

    window.onloadTurnstileCallback = () => {
      if (!window.turnstile || !turnstileRef.current) return;

      try {
        if (!turnstileWidgetId.current) {
          turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
            sitekey: CLOUDFLARE_SITE_KEY,
            callback: (token) => {
              console.log('Turnstile verification successful');
              setTurnstileToken(token);
            },
            'error-callback': () => {
              console.error('Turnstile verification failed');
              setTurnstileError(true);
            }
          });
        }
      } catch (err) {
        console.error('Error rendering Turnstile widget:', err);
        setTurnstileError(true);
      }
    };

    if (window.turnstile && turnstileRef.current) {
      window.onloadTurnstileCallback();
    }

    return () => {
      if (window.turnstile && turnstileWidgetId.current) {
        try {
          window.turnstile.reset(turnstileWidgetId.current);
        } catch (error) {
          console.warn('Error during Turnstile cleanup:', error);
        }
      }
    };
  }, [CLOUDFLARE_SITE_KEY, isDevelopment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const getFileExtension = (fileName) => {
    const match = fileName.match(/(\.[^.]+)$/);
    return match ? match[1].toLowerCase() : '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSubmitStatus({
        type: 'danger',
        message: `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum ${MAX_FILE_SIZE_MB}MB`
      });
      e.target.value = '';
      return;
    }

    // Validate file extension
    const extension = getFileExtension(file.name);
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setSubmitStatus({
        type: 'danger',
        message: `File type ${extension} not allowed. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`
      });
      e.target.value = '';
      return;
    }

    setSelectedFile(file);
    setUploadedFileKey(null);
    setSubmitStatus(null);
  };

  const handleFileUpload = async (file, formDataPayload) => {
    try {
      setUploading(true);
      setUploadProgress(10);

      // Step 1: Request presigned URL (includes form data and sends email)
      const response = await fetch(UPLOAD_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type || 'application/octet-stream',
          turnstileToken: turnstileToken,
          // Include all form data
          ...formDataPayload
        })
      });

      setUploadProgress(30);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to get upload URL');
      }

      const { uploadUrl, s3Key } = await response.json();

      setUploadProgress(50);

      // Step 2: Upload file directly to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        }
      });

      setUploadProgress(90);

      if (!uploadResponse.ok) {
        throw new Error('File upload to S3 failed');
      }

      setUploadProgress(100);
      setUploadedFileKey(s3Key);
      setUploading(false);

      return s3Key;

    } catch (error) {
      console.error('Upload error:', error);
      setUploading(false);
      setUploadProgress(0);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus(null);

    // Require token in production when site key is configured
    if (!turnstileToken && !isDevelopment && CLOUDFLARE_SITE_KEY) {
      setSubmitStatus({
        type: 'danger',
        message: 'Please complete the security verification.'
      });
      setLoading(false);
      return;
    }

    // If 3D printing service is selected, require a file
    if (formData.serviceType === '3d-printing' && !selectedFile && !uploadedFileKey) {
      setSubmitStatus({
        type: 'danger',
        message: 'Please upload a CAD file for 3D printing service.'
      });
      setLoading(false);
      return;
    }

    try {
      // Prepare form data payload
      const formDataPayload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        serviceType: formData.serviceType,
        material: formData.material,
        quantity: formData.quantity,
        projectDescription: formData.projectDescription
      };

      // If file needs to be uploaded, do it now (this also sends the email)
      if (selectedFile && !uploadedFileKey) {
        await handleFileUpload(selectedFile, formDataPayload);
      } else if (uploadedFileKey) {
        // File already uploaded, just show success
        console.log('File already uploaded:', uploadedFileKey);
      } else {
        // No file upload - this is for "modeling-and-printing" service
        // Send form data via contact API
        const contactData = {
          ...formData,
          message: formData.projectDescription,
          token: turnstileToken || 'dev-token'
        };

        const response = await fetch(CONTACT_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(contactData)
        });

        if (!response.ok) {
          throw new Error('Form submission failed');
        }
      }

      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your request! We will get back to you soon with a quote.'
      });

      setFormSubmitted(true);

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        serviceType: '3d-printing',
        material: '',
        quantity: 1,
        projectDescription: ''
      });
      setSelectedFile(null);
      setUploadedFileKey(null);
      setUploadProgress(0);

      // Reset Turnstile
      if (!isDevelopment && CLOUDFLARE_SITE_KEY && window.turnstile && turnstileWidgetId.current) {
        setTimeout(() => {
          try {
            window.turnstile.reset(turnstileWidgetId.current);
            setTurnstileToken(null);
          } catch (error) {
            console.warn('Could not reset Turnstile:', error);
          }
        }, 2000);
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'danger',
        message: 'There was an error submitting your request. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="product-dev-contact-form">
      <Form onSubmit={handleSubmit}>
        {/* Contact Information */}
        <Row>
          <Col md={6} className="mb-3">
            <Form.Group controlId="name">
              <Form.Label>Your Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter your name"
              />
            </Form.Group>
          </Col>
          <Col md={6} className="mb-3">
            <Form.Group controlId="email">
              <Form.Label>Your Email <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="Enter your email"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6} className="mb-3">
            <Form.Group controlId="phone">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
              />
            </Form.Group>
          </Col>
          <Col md={6} className="mb-3">
            <Form.Group controlId="company">
              <Form.Label>Company Name</Form.Label>
              <Form.Control
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Enter your company name"
              />
            </Form.Group>
          </Col>
        </Row>

        {/* Service Selection */}
        <Form.Group controlId="serviceType" className="mb-3">
          <Form.Label>Service Type <span className="text-danger">*</span></Form.Label>
          <Form.Select
            name="serviceType"
            value={formData.serviceType}
            onChange={handleChange}
            required
          >
            <option value="3d-printing">3D Printing Only (I have a CAD file)</option>
            <option value="modeling-and-printing">CAD Modeling + 3D Printing (Full Service)</option>
          </Form.Select>
        </Form.Group>

        {/* File Upload - only show for 3D printing service */}
        {formData.serviceType === '3d-printing' && (
          <Form.Group controlId="file" className="mb-3">
            <Form.Label>Upload CAD File <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="file"
              onChange={handleFileChange}
              accept=".stl,.obj,.step,.stp,.iges,.igs,.3mf,.amf,.brep,.zip"
              disabled={uploading}
            />
            <Form.Text className="text-muted">
              Supported formats: {ALLOWED_EXTENSIONS.join(', ')} (Max {MAX_FILE_SIZE_MB}MB)
            </Form.Text>
            {selectedFile && !uploadedFileKey && (
              <div className="file-info mt-2">
                <FaUpload /> {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)}MB)
              </div>
            )}
            {uploadedFileKey && (
              <div className="file-success mt-2">
                <FaCheckCircle /> File uploaded successfully
              </div>
            )}
            {uploading && (
              <div className="mt-2">
                <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated />
              </div>
            )}
          </Form.Group>
        )}

        {/* Project Details */}
        <Row>
          <Col md={6} className="mb-3">
            <Form.Group controlId="material">
              <Form.Label>Preferred Material</Form.Label>
              <Form.Select
                name="material"
                value={formData.material}
                onChange={handleChange}
              >
                <option value="">Select material (optional)</option>
                <option value="PLA">PLA</option>
                <option value="ABS">ABS</option>
                <option value="PETG">PETG</option>
                <option value="TPU">TPU (Flexible)</option>
                <option value="Nylon">Nylon</option>
                <option value="Other">Other</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6} className="mb-3">
            <Form.Group controlId="quantity">
              <Form.Label>Quantity</Form.Label>
              <Form.Control
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                min="1"
                placeholder="1"
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group controlId="projectDescription" className="mb-4">
          <Form.Label>Project Description <span className="text-danger">*</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            name="projectDescription"
            value={formData.projectDescription}
            onChange={handleChange}
            required
            placeholder={
              formData.serviceType === '3d-printing'
                ? 'Describe your project, any special requirements, preferred colors, etc.'
                : 'Describe what you want to build - be as detailed as possible about dimensions, features, and intended use.'
            }
          />
        </Form.Group>

        {/* Turnstile */}
        <div className="turnstile-container mb-4">
          {isDevelopment ? (
            <div className="text-center mb-2">
              <Alert variant="info" className="py-2">
                <small>Development mode: Turnstile verification bypassed</small>
              </Alert>
            </div>
          ) : (
            <>
              {turnstileToken && (
                <div className="turnstile-success text-center mb-2">
                  <Alert variant="success" className="py-2">
                    <span className="d-flex align-items-center justify-content-center">
                      <FaCheckCircle className="me-2" />
                      Verification complete
                    </span>
                  </Alert>
                </div>
              )}
              {turnstileError && !turnstileToken && (
                <div className="turnstile-error text-center mb-2">
                  <Alert variant="warning" className="py-2">
                    <small>Verification failed. Please try again or refresh the page.</small>
                  </Alert>
                </div>
              )}
              {!turnstileToken && (
                <div className="turnstile-success text-center mb-2">
                  <div ref={turnstileRef} className="d-flex justify-content-center"></div>
                </div>
              )}
            </>
          )}
        </div>

        {!formSubmitted && (
          <Button
            variant="primary"
            type="submit"
            disabled={loading || uploading || (!turnstileToken && !isDevelopment && CLOUDFLARE_SITE_KEY)}
            className="w-100"
            style={{ borderRadius: '6px', padding: '12px' }}
          >
            {loading ? 'Submitting...' :
             uploading ? 'Uploading File...' :
             isDevelopment ? 'Submit Request (Dev Mode)' :
             !CLOUDFLARE_SITE_KEY ? 'Submit Request' :
             turnstileToken ? 'Submit Request' : 'Please Complete Verification'}
          </Button>
        )}

        {submitStatus && (
          <Alert variant={submitStatus.type} className="mt-3">
            {submitStatus.message}
          </Alert>
        )}
      </Form>
    </div>
  );
};

export default ProductDevContactForm;
